import { motion } from 'framer-motion';
import { Calendar, CheckCircle2, ChevronDown, Loader2, XCircle } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { roasterService } from '@/services';
import type { RoasterMonth, StaffRoaster } from '@/types';
import { extractErrorMessage, showErrorToast } from '@/utils';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export function Roaster() {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const [loading, setLoading] = useState(true);
  const [availableMonths, setAvailableMonths] = useState<RoasterMonth[]>([]);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [currentRoaster, setCurrentRoaster] = useState<StaffRoaster | null>(null);

  useEffect(() => {
    fetchAvailableMonths();
  }, []);

  useEffect(() => {
    fetchRoasterForMonth(selectedYear, selectedMonth);
  }, [selectedYear, selectedMonth]);

  const fetchAvailableMonths = async () => {
    try {
      const months = await roasterService.getMyAvailableMonths();
      setAvailableMonths(months);
    } catch (error) {
      showErrorToast(extractErrorMessage(error, 'Failed to load available months'));
    }
  };

  const fetchRoasterForMonth = async (year: number, month: number) => {
    setLoading(true);
    try {
      const roaster = await roasterService.getMyRoaster(year, month);
      setCurrentRoaster(roaster);
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        setCurrentRoaster(null);
      } else {
        showErrorToast(extractErrorMessage(error, 'Failed to load roaster'));
      }
    } finally {
      setLoading(false);
    }
  };

  const monthOptions = useMemo(() => {
    const options: Array<{ year: number; month: number; label: string }> = [];
    const allMonths = new Set(availableMonths.map((m) => `${m.year}-${m.month}`));

    for (let i = -12; i <= 12; i++) {
      const date = new Date(currentYear, currentMonth - 1 - i, 1);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const key = `${year}-${month}`;

      if (allMonths.has(key)) {
        options.push({
          year,
          month,
          label: `${MONTHS[month - 1]} ${year}`,
        });
      }
    }

    return options.sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.month - a.month;
    });
  }, [availableMonths, currentYear, currentMonth]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
          <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
            My Roaster
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1 text-xs font-medium">
            View your scheduled work days for the month.
          </p>
        </motion.div>

        <div className="flex items-center gap-3">
          <div className="relative">
            <select
              value={`${selectedYear}-${selectedMonth}`}
              onChange={(e) => {
                const [year, month] = e.target.value.split('-').map(Number);
                setSelectedYear(year);
                setSelectedMonth(month);
              }}
              className="appearance-none bg-white dark:bg-neolab-forest border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2 pr-10 text-sm font-bold text-slate-900 dark:text-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-neolab-green"
            >
              {monthOptions.map((opt) => (
                <option key={`${opt.year}-${opt.month}`} value={`${opt.year}-${opt.month}`}>
                  {opt.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center min-h-[60vh]">
          <Loader2 className="w-12 h-12 text-neolab-green animate-spin" />
        </div>
      ) : currentRoaster ? (
        <div className="bg-white dark:bg-neolab-forest rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="mb-4">
            <h2 className="text-xl font-black text-slate-900 dark:text-white">
              {MONTHS[selectedMonth - 1]} {selectedYear} Roaster
            </h2>
          </div>
          <div className="space-y-2">
            {currentRoaster.roaster.map((day) => (
              <div
                key={day.dayNumber}
                className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700"
              >
                <div className="w-28 text-sm font-black text-slate-900 dark:text-white">
                  {day.dayNumber}. {day.day}
                </div>
                <div className="flex-1 flex items-center gap-2">
                  {day.hasEmployee ? (
                    <>
                      <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                      <span className="text-emerald-600 dark:text-emerald-400 font-bold text-sm">
                        Scheduled
                      </span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-5 h-5 text-slate-400" />
                      <span className="text-slate-400 font-bold text-sm">Not Scheduled</span>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-neolab-forest rounded-xl border border-slate-200 dark:border-slate-700 p-8 text-center">
          <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-bold text-sm">No roaster found for this month</p>
        </div>
      )}
    </div>
  );
}
